import { Alert, Box, Center, Group, Loader, Stack, Text } from "@mantine/core"
import { useEffect, useRef, useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { loadGoogleIdentityScript } from "atlas_forecasting_app/lib/google_identity"

const GOOGLE_BUTTON_OPTIONS = {
  theme: "outline",
  size: "large",
  text: "continue_with",
  shape: "rectangular",
  logo_alignment: "left",
}

export function GoogleIdentityButton({ clientId, origin, onAuthResult, fullWidth = false }) {
  const containerRef = useRef(null)
  const onAuthResultRef = useRef(onAuthResult)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  onAuthResultRef.current = onAuthResult

  useEffect(() => {
    let cancelled = false

    const resolvedOrigin = origin || `${window.location.pathname}${window.location.search}${window.location.hash}`

    const renderGoogleButton = async () => {
      if (!clientId) {
        setLoading(false)
        setError("Google sign-in is not configured.")
        return
      }

      setLoading(true)
      setError("")

      try {
        const google = await loadGoogleIdentityScript()
        if (cancelled || !containerRef.current) return

        containerRef.current.innerHTML = ""
        google.accounts.id.initialize({
          client_id: clientId,
          ux_mode: "popup",
          callback: async (response) => {
            const credential = response?.credential
            if (!credential) {
              setError("Google sign-in did not return a credential.")
              return
            }

            setSubmitting(true)
            setError("")

            try {
              const data = await apiFetch("/api/auth/google", {
                method: "POST",
                body: {
                  credential,
                  return_to: resolvedOrigin,
                },
              })
              if (!cancelled) onAuthResultRef.current?.(data)
            } catch (e) {
              if (!cancelled) setError(e.message)
            } finally {
              if (!cancelled) setSubmitting(false)
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        const width = fullWidth ? Math.max(containerRef.current.clientWidth || 0, 240) : 240
        google.accounts.id.renderButton(containerRef.current, { ...GOOGLE_BUTTON_OPTIONS, width })
        setLoading(false)
      } catch (e) {
        if (cancelled) return

        setLoading(false)
        setError(e.message)
      }
    }

    renderGoogleButton()

    return () => {
      cancelled = true
      if (containerRef.current) containerRef.current.innerHTML = ""
    }
  }, [clientId, fullWidth, origin])

  return (
    <Stack gap="sm">
      {error ? (
        <Alert color="red" title="Something went wrong" variant="light">
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <Center py="sm">
          <Group gap="sm">
            <Loader size="sm" />
            <Text c="dimmed">Loading Google sign-in...</Text>
          </Group>
        </Center>
      ) : null}
      {submitting ? <Text size="sm" c="dimmed">Signing in with Google...</Text> : null}
      <Box ref={containerRef} style={{ width: fullWidth ? "100%" : "auto", minHeight: 40 }} data-testid="google-identity-button" />
    </Stack>
  )
}
